import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Code2,
  Layers,
  Gauge,
  Smartphone,
  Search,
  ShieldCheck,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";

const TECH_STACK = [
  { name: "WordPress", desc: "Flexible CMS for content-heavy sites" },
  { name: "Custom React", desc: "Premium, app-grade experiences" },
  { name: "Tailwind CSS", desc: "Design system &amp; consistent UI" },
  { name: "Cloudflare", desc: "CDN, security &amp; edge speed" },
  { name: "LiteSpeed", desc: "Hosting tuned for performance" },
  { name: "Rank Math", desc: "Technical SEO baked in" },
];

const FEATURES = [
  {
    icon: Code2,
    title: "Custom-coded foundation",
    desc: "No bloated templates. Lean, semantic markup that loads in under a second.",
  },
  {
    icon: Layers,
    title: "Conversion-led design",
    desc: "Every section, button and form is architected around a single business goal.",
  },
  {
    icon: Gauge,
    title: "99 / 100 PageSpeed",
    desc: "Performance budget enforced from day one — Core Web Vitals in the green.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first build",
    desc: "Designed phone-first, then scaled up to desktop. Real device tested.",
  },
  {
    icon: Search,
    title: "SEO baked in",
    desc: "Schema, meta, internal linking and content structure ready to rank from launch.",
  },
  {
    icon: ShieldCheck,
    title: "Secure &amp; future-proof",
    desc: "Hardened infrastructure, automated backups, monitoring &amp; patching.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Discovery & Audit",
    desc: "Stakeholder interviews, brand audit and competitor teardown. We surface what's actually moving revenue.",
  },
  {
    n: "02",
    title: "Strategy & Sitemap",
    desc: "Information architecture, user journeys and KPI mapping. Approved before a single pixel is drawn.",
  },
  {
    n: "03",
    title: "Design & Prototype",
    desc: "High-fidelity Figma prototypes — every state, every breakpoint. Weekly review sessions.",
  },
  {
    n: "04",
    title: "Build & QA",
    desc: "Custom-coded build with continuous QA across browsers, devices and real-world conditions.",
  },
  {
    n: "05",
    title: "Launch & Optimize",
    desc: "Soft launch, analytics + Search Console wiring, then 30 days of post-launch optimization.",
  },
];

const INCLUDES = [
  "Custom design & development (up to 10 pages)",
  "Mobile-first responsive build",
  "On-page SEO + technical SEO foundation",
  "Schema markup (Service, FAQ, LocalBusiness)",
  "Contact form & lead capture",
  "Analytics + Search Console setup",
  "Speed optimization (sub-1s target)",
  "Content migration support",
  "30 days of post-launch support",
  "Full ownership of code &amp; assets",
];

const SHOWCASE = [
  {
    name: "Sara Palmer DDS",
    industry: "Dental",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2026/03/desktop-4.png",
    slug: "sara-palmer-dds",
  },
  {
    name: "MyRoofNexus",
    industry: "Roofing",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2025/09/desktop-1-1.png",
    slug: "myroofnexus",
  },
  {
    name: "Build With Koda",
    industry: "Construction",
    img: "https://portfolio.echowingsmedia.com/wp-content/uploads/2025/09/desktop-1.png",
    slug: "buildwithkoda",
  },
];

const FAQS = [
  {
    q: "How long does a custom website take to build?",
    a: "Most projects ship in 3–6 weeks from kickoff. We share a transparent timeline after discovery, with weekly demos so you always know exactly where things stand.",
  },
  {
    q: "Do I own the website and all the code?",
    a: "100%. All code, designs, copy and assets transfer to you on launch. No proprietary platforms, no lock-in, ever.",
  },
  {
    q: "What if I need changes after launch?",
    a: "You get 30 days of post-launch support included. After that, you can either manage the site yourself or join one of our optional care plans for hands-off maintenance.",
  },
  {
    q: "Can you migrate content from my existing site?",
    a: "Yes — we handle content migration as part of every engagement. We&apos;ll also flag any pages worth rewriting for SEO impact.",
  },
  {
    q: "Will my new site rank better on Google?",
    a: "Speed, structure and on-page SEO are baked in from day one. Combined with our SEO + GEO service, most clients see meaningful ranking jumps within 90 days of launch.",
  },
];

export default function ServiceWebsiteDevPage() {
  return (
    <PageShell testId="service-website-dev-page">
      {/* Hero */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-0 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/services"
            data-testid="service-detail-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              <Code2 className="w-3.5 h-3.5" />
              Website Development
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              Premium websites,{" "}
              <span className="text-gradient-brand">
                engineered to convert.
              </span>
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl">
              Custom-coded, conversion-engineered websites that load in under a
              second, rank on day one, and quietly out-perform agency builds
              costing 4× more.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/book-audit"
                data-testid="service-cta-audit"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all"
              >
                Get Free Website Audit
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/pricing"
                data-testid="service-cta-pricing"
                className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all backdrop-blur-md"
              >
                View Pricing
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-2xl">
              {[
                { v: "0.9s", l: "Avg. load time" },
                { v: "99/100", l: "PageSpeed score" },
                { v: "3–6", l: "Weeks to launch" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-3"
                >
                  <div className="font-display text-2xl font-extrabold text-gradient-brand">
                    {s.v}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="section pt-0" data-testid="service-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              What you get
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              Engineered for performance.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`service-feature-${i}`}
                className="rounded-3xl border border-white/10 bg-white/[0.025] p-7 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shadow-[0_8px_20px_rgba(0,229,255,0.2)]">
                  <f.icon className="w-5 h-5 text-black" />
                </div>
                <h3
                  className="mt-5 font-display text-xl font-semibold"
                  dangerouslySetInnerHTML={{ __html: f.title }}
                />
                <p
                  className="mt-2 text-sm text-white/55 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: f.desc }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section pt-0" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              How we build
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              A repeatable, transparent process.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`service-process-${i}`}
                className="relative rounded-2xl border border-white/10 bg-white/[0.025] p-5"
              >
                <div className="font-display text-3xl font-extrabold text-white/15">
                  {p.n}
                </div>
                <div className="mt-3 font-display text-lg font-semibold">
                  {p.title}
                </div>
                <p className="mt-1.5 text-sm text-white/55 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="section pt-0" data-testid="service-stack">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                  Our stack
                </div>
                <h3 className="mt-2 font-display text-3xl font-bold leading-tight">
                  We pick the right tool — never the same one twice.
                </h3>
                <p className="mt-4 text-white/55">
                  We don&apos;t force-fit every client onto one platform. We
                  match technology to your business goals, content needs and
                  team capacity.
                </p>
              </div>
              <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                {TECH_STACK.map((t) => (
                  <li
                    key={t.name}
                    className="flex items-start gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.025]"
                  >
                    <span className="mt-0.5 w-2 h-2 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 shrink-0" />
                    <div>
                      <div className="font-semibold text-white text-sm">
                        {t.name}
                      </div>
                      <div
                        className="text-xs text-white/55"
                        dangerouslySetInnerHTML={{ __html: t.desc }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Includes */}
      <section className="section pt-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl p-[1px] bg-gradient-to-br from-blue-600/40 via-cyan-400/30 to-transparent">
            <div className="rounded-[calc(1.5rem-1px)] bg-[#0a0a0a] p-8 md:p-12">
              <div className="grid md:grid-cols-3 gap-10">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                    What&apos;s included
                  </div>
                  <h3 className="mt-2 font-display text-3xl font-bold leading-tight">
                    Every project. Every time.
                  </h3>
                  <Link
                    to="/pricing"
                    data-testid="service-includes-pricing"
                    className="mt-6 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold text-sm"
                  >
                    Compare packages <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                  {INCLUDES.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-3 text-white/85"
                    >
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-black" />
                      </span>
                      <span
                        className="text-sm"
                        dangerouslySetInnerHTML={{ __html: it }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="section pt-0" data-testid="service-showcase">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
                Recent builds
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold">
                Custom websites we&apos;ve shipped.
              </h2>
            </div>
            <Link
              to="/projects"
              className="hidden md:inline-flex text-sm text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SHOWCASE.map((s, i) => (
              <Link
                key={s.slug}
                to={`/projects`}
                data-testid={`service-showcase-${i}`}
                className="group rounded-3xl border border-white/10 bg-white/[0.025] overflow-hidden hover:border-cyan-500/30 transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#0a0a0a]">
                  <img
                    src={s.img}
                    alt={s.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                    {s.industry}
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold">
                    {s.name}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section pt-0" data-testid="service-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              FAQ
            </div>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight">
              Website development, answered.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={f.q}
                data-testid={`service-faq-${i}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.025] open:border-cyan-500/30 transition-colors"
              >
                <summary className="list-none cursor-pointer px-6 py-5 flex items-center justify-between gap-3 font-display text-lg font-semibold">
                  {f.q}
                  <span className="text-cyan-400 transition-transform group-open:rotate-45 text-2xl leading-none">
                    +
                  </span>
                </summary>
                <div
                  className="px-6 pb-5 text-white/65 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: f.a }}
                />
              </details>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
